import { Button, ExternalLink, Text } from '@ui/themed';
import { t } from 'i18next';
import { useEnv } from '../../../context/env-context';
import { useHandlers, useUser } from '../../../context/page-context';
import type { I18nPrefix } from '../../../i18n/prefix';
import { isShopify, isWix, themeSetupUrl } from '../utils/platform';
import { buildCoreSnippet } from '../utils/snippets';
import { CodeSnippet } from './code-snippet';
import { SparkJsVersionCard } from './sparkjs-version-card';
import { WidgetSection } from './widget-section';

const PAGE = 'features:storefront_widgets.pages.storefront_widgets_page.core' satisfies I18nPrefix;

const SHOPIFY_INSTALL_OPTIONS = ['request', 'app_block', 'manual'] as const;

export function CoreTab() {
  const env = useEnv();
  const { isSparkStaff } = useUser();
  const { navigationHandler } = useHandlers();

  const shopify = isShopify(env.platform);
  const showCodeSection = isSparkStaff || (!shopify && !isWix(env.platform));
  const themeSetupAdminUrl = themeSetupUrl(env);

  return (
    <div className="flex flex-col gap-5">
      {showCodeSection && (
        <WidgetSection
          title={t(`${PAGE}.title`)}
          subtitle={t(`${PAGE}.description`)}
          learnMoreUrl="https://docs.sparklayer.io/frontend-integration#5YjYK"
        >
          <CodeSnippet code={buildCoreSnippet(env)} />
        </WidgetSection>
      )}

      {shopify && (
        <WidgetSection
          title={t(`${PAGE}.shopify.title`)}
          subtitle={t(`${PAGE}.shopify.description`)}
        >
          <ul className="list-disc space-y-2 ps-5 marker:text-spark-text-secondary">
            {SHOPIFY_INSTALL_OPTIONS.map((option) => (
              <li key={option}>
                <Text size="sm" bold>
                  {t(`${PAGE}.shopify.options.${option}.title`)}
                </Text>
                <Text size="sm" variant="secondary">
                  {t(`${PAGE}.shopify.options.${option}.description`)}
                </Text>
              </li>
            ))}
          </ul>
          <div className="flex flex-wrap items-center gap-3">
            {themeSetupAdminUrl && (
              <Button
                icon="externalLink"
                onClick={() =>
                  navigationHandler.navigate(themeSetupAdminUrl, { absolute: true, newTab: true })
                }
              >
                {t(`${PAGE}.shopify.open_theme_setup`)}
              </Button>
            )}
            <ExternalLink link="https://docs.sparklayer.io/frontend-integration">
              {t(`${PAGE}.shopify.read_guide`)}
            </ExternalLink>
          </div>
        </WidgetSection>
      )}

      <SparkJsVersionCard />
    </div>
  );
}
